
import { Compass, Wind, RotateCw } from "lucide-react";
import { useCamera } from "../hooks/useCamera";
import type { CinematicStyle } from "../stores/types";
import { cn } from "@/lib/utils";

interface CameraModeConfig {
  id: "free" | CinematicStyle;
  label: string;
  icon: React.ReactNode;
  description: string;
}

const cameraModes: CameraModeConfig[] = [
  {
    id: "free",
    label: "Free",
    icon: <Compass size={18} />,
    description: "Manual orbit/pan/zoom",
  },
  {
    id: "ambient",
    label: "Ambient",
    icon: <Wind size={18} />,
    description: "Stationary breathing with gentle sway",
  },
  {
    id: "orbit",
    label: "Orbit",
    icon: <RotateCw size={18} />,
    description: "Rotating fly-through around scene",
  },
];

/**
 * View mode switcher with 3 direct camera modes:
 * - Free: Manual control
 * - Ambient: Cinematic stationary breathing
 * - Orbit: Cinematic rotating fly-through
 *
 * Single-click activation — no sub-menu needed.
 */
export function ViewPerspectiveSwitcher() {
  const { viewPreset, cinematicStyle, setViewPreset, setCinematicStyle } =
    useCamera();

  // Determine which mode is currently active
  const activeModeId = viewPreset === "free" ? "free" : cinematicStyle;

  const handleModeClick = (modeId: CameraModeConfig["id"]) => {
    if (modeId === "free") {
      setViewPreset("free");
    } else {
      setViewPreset("cinematic");
      setCinematicStyle(modeId);
    }
  };

  return (
    <div className="flex items-center gap-0.5 md:gap-1 bg-white/90 backdrop-blur-sm border border-sands-200 rounded-lg p-0.5 shadow-sm">
      {cameraModes.map(({ id, label, icon, description }) => {
        const isActive = activeModeId === id;
        return (
          <button
            key={id}
            onClick={() => handleModeClick(id)}
            className={cn(
              "min-w-[40px] min-h-[40px] md:min-w-[36px] md:min-h-[36px] flex items-center justify-center rounded-md transition-colors",
              isActive
                ? "bg-sands-200 text-sands-800"
                : "text-sands-500 hover:text-sands-700 hover:bg-sands-100",
            )}
            title={`${label} — ${description}`}
          >
            {icon}
          </button>
        );
      })}
    </div>
  );
}
